import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { companyApi } from '../api/companyApi';
import Badge from '../components/common/Badge';
import EditCompanyModal from '../components/company/EditCompanyModal';
import { formatDate } from '../utils/formatDate';
import { ArrowLeft, Building2, Users, Hash, Shield, Mail, Phone, Calendar, Edit, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

const CompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchCompany = async () => {
    try {
      const res = await companyApi.getCompanyById(id);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load company details');
      navigate('/companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompany();
  }, [id, navigate]);

  const handleUpdateCompany = async (companyId, formData) => {
    setSubmitting(true);
    try {
      await companyApi.updateCompany(companyId, formData);
      toast.success('Company tenant and employee limit updated!');
      setIsEditOpen(false);
      fetchCompany();
    } catch (err) {
      toast.error(err.message || 'Failed to update company');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !data) {
    return <div className="py-20 text-center text-xs text-slate-500">Loading company profile...</div>;
  }

  const { company, employees = [], idConfig } = data;
  const limit = company.employeeLimit || 50;
  const current = company.currentEmployeeCount || 0;
  const ratio = (current / limit) * 100;

  return (
    <div className="space-y-6 animate-fadeIn">
      <button
        onClick={() => navigate('/companies')}
        className="inline-flex items-center space-x-2 text-xs font-medium text-slate-500 hover:text-slate-900 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Companies Directory</span>
      </button>

      {/* Header card */}
      <div className="p-6 rounded-3xl bg-white border border-slate-100 shadow-sm shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-center space-x-4">
          <img
            src={company.logo || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=150&auto=format&fit=crop&q=80'}
            alt={company.name}
            className="w-20 h-20 rounded-2xl object-cover border border-slate-700"
          />
          <div>
            <div className="flex items-center space-x-3">
              <h2 className="text-2xl font-extrabold text-slate-900 font-display">{company.name}</h2>
              <Badge variant={company.status}>{company.status}</Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1">{company.industry}</p>
            <p className="text-xs text-slate-500 mt-1 flex items-center space-x-3">
              <span>Onboarded: {formatDate(company.onboardedAt)}</span>
              <span>•</span>
              <span>ID Prefix: <strong className="text-green-700 font-mono">{company.employeeIdPrefix}</strong></span>
            </p>
          </div>
        </div>

        <button
          onClick={() => setIsEditOpen(true)}
          className="px-4 py-2.5 rounded-xl bg-green-700 hover:bg-green-600 text-white font-semibold text-xs shadow-lg shadow-green-700/30 flex items-center space-x-2 transition"
        >
          <Edit className="w-4 h-4" />
          <span>Edit Limit & Profile</span>
        </button>
      </div>

      {/* Grid Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="p-6 rounded-3xl bg-white border border-slate-100 shadow-sm shadow-xl">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Employee Limit Cap</p>
          <p className="text-3xl font-extrabold text-slate-900 font-display mt-2">{limit} Max</p>
          <p className="text-xs text-slate-500 mt-3 font-mono">
            Used: <strong className="text-green-700">{current}</strong> / {limit}
          </p>
        </div>

        <div className="p-6 rounded-3xl bg-white border border-slate-100 shadow-sm shadow-xl">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Active Employees</p>
          <p className="text-3xl font-extrabold text-emerald-600 font-display mt-2">{current}</p>
          <p className="text-[11px] text-slate-500 mt-3 flex items-center space-x-1">
            <Users className="w-3.5 h-3.5 text-emerald-600" />
            <span>{limit - current} slots remaining</span>
          </p>
        </div>

        <div className="p-6 rounded-3xl bg-white border border-slate-100 shadow-sm shadow-xl">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Current Sequence</p>
          <p className="text-3xl font-mono font-extrabold text-green-700 font-display mt-2">
            #{idConfig ? idConfig.currentSequence : 0}
          </p>
          <p className="text-[11px] text-slate-500 mt-3 flex items-center space-x-1">
            <Sparkles className="w-3.5 h-3.5 text-green-700" />
            <span>Next ID: {idConfig ? `${idConfig.prefix}${String(idConfig.currentSequence + 1).padStart(4, '0')}` : 'N/A'}</span>
          </p>
        </div>
      </div>

      {/* Admin Section */}
      {company.adminId && (
        <div className="p-6 rounded-3xl bg-white border border-slate-100 shadow-sm shadow-xl">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4 flex items-center space-x-2">
            <Shield className="w-4 h-4 text-green-700" />
            <span>Assigned Company Admin</span>
          </h3>
          <div className="flex items-center space-x-4">
            <img
              src={company.adminId.avatar || 'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=150&auto=format&fit=crop&q=80'}
              alt={company.adminId.name}
              className="w-12 h-12 rounded-full object-cover border border-slate-700"
            />
            <div>
              <p className="text-base font-semibold text-slate-900">{company.adminId.name}</p>
              <p className="text-xs text-slate-500">{company.adminId.email}</p>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <EditCompanyModal
        company={company}
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSubmit={handleUpdateCompany}
        loading={submitting}
      />
    </div>
  );
};

export default CompanyDetails;
